/**
 * Markdown Image Processing
 *
 * Converts custom {{image:filename|width=N|caption=text}} syntax to HTML.
 * Images are served from local /images/ directory (downloaded from Cloudinary during build).
 *
 * Size variants are generated during build:
 * - _w200, _w400, _w600, _w800, _w1200, _w1440
 */

// Standard image size variants (must match download-cloudinary-images.ts)
const IMAGE_SIZES = [200, 400, 600, 800, 1200, 1440];

interface ImageOptions {
  filename: string;
  width?: number;
  caption?: string;
}

function parseImageTag(tag: string): ImageOptions | null {
  // Parse {{image:filename|width=800|caption=text}}
  const match = tag.match(/\{\{image:([^|}\s]+)(?:\|([^}]*))?\}\}/);
  if (!match) return null;

  const filename = match[1];
  const optionsStr = match[2] || '';

  const widthMatch = optionsStr.match(/width=(\d+)/);
  const captionMatch = optionsStr.match(/caption=([^|]+)/);

  return {
    filename,
    width: widthMatch ? parseInt(widthMatch[1]) : 800,
    caption: captionMatch ? captionMatch[1].trim() : undefined
  };
}

function getClosestSize(targetWidth: number): number {
  // Find the smallest size that is >= targetWidth
  for (const size of IMAGE_SIZES) {
    if (size >= targetWidth) return size;
  }
  // If target is larger than all sizes, return the largest
  return IMAGE_SIZES[IMAGE_SIZES.length - 1];
}

function generateLocalImageUrl(filename: string, width: number): string {
  const size = getClosestSize(width);
  return `/images/${filename}_w${size}.webp`;
}

function generateSrcSet(filename: string): string {
  // Generate srcset with all available sizes
  return IMAGE_SIZES
    .map(size => `/images/${filename}_w${size}.webp ${size}w`)
    .join(', ');
}

/**
 * Process custom image tags in markdown content.
 * Converts {{image:filename|width=800|caption=text}} to HTML img/figure elements
 * with local image paths and responsive srcset.
 *
 * Images must be downloaded during build via download-cloudinary-images.ts
 */
export function processMarkdownImages(content: string): string {
  if (!content) return '';

  return content.replace(
    /\{\{image:[^}]+\}\}/g,
    (match) => {
      const options = parseImageTag(match);
      if (!options) return match;

      const width = options.width || 800;
      const url = generateLocalImageUrl(options.filename, width);
      const srcset = generateSrcSet(options.filename);
      const sizes = `(max-width: ${width}px) 100vw, ${width}px`;

      if (options.caption) {
        return `<figure class="markdown-image">
<img src="${url}" srcset="${srcset}" sizes="${sizes}" alt="${options.caption}" loading="lazy" />
<figcaption>${options.caption}</figcaption>
</figure>`;
      }

      return `<img src="${url}" srcset="${srcset}" sizes="${sizes}" alt="" class="markdown-image" loading="lazy" />`;
    }
  );
}
